import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetActions, CNetResult } from './_cnet';

// Resistor color codes
const COLORS = [
  { name: 'black', hex: '#111', fg: '#fff', digit: 0, mult: 1, tol: null },
  { name: 'brown', hex: '#8b4513', fg: '#fff', digit: 1, mult: 10, tol: 1 },
  { name: 'red', hex: '#dc2626', fg: '#fff', digit: 2, mult: 100, tol: 2 },
  { name: 'orange', hex: '#ea580c', fg: '#fff', digit: 3, mult: 1_000, tol: 0.05 },
  { name: 'yellow', hex: '#facc15', fg: '#000', digit: 4, mult: 10_000, tol: 0.02 },
  { name: 'green', hex: '#65a30d', fg: '#fff', digit: 5, mult: 100_000, tol: 0.5 },
  { name: 'blue', hex: '#3b82f6', fg: '#fff', digit: 6, mult: 1_000_000, tol: 0.25 },
  { name: 'violet', hex: '#8b5cf6', fg: '#fff', digit: 7, mult: 10_000_000, tol: 0.1 },
  { name: 'grey', hex: '#6b7280', fg: '#fff', digit: 8, mult: 100_000_000, tol: 0.01 },
  { name: 'white', hex: '#ffffff', fg: '#000', digit: 9, mult: 1_000_000_000, tol: null },
];
const MULT_EXTRA = [
  { name: 'gold', hex: '#d4af37', fg: '#000', mult: 0.1, tol: 5 },
  { name: 'silver', hex: '#c0c0c0', fg: '#000', mult: 0.01, tol: 10 },
];
const TOL_ONLY = [
  ...COLORS.filter(c => c.tol != null),
  ...MULT_EXTRA,
];
const TOL_LETTER = { 1: 'F', 2: 'G', 0.5: 'D', 0.25: 'C', 0.1: 'B', 0.05: 'W', 0.02: 'P', 0.01: 'L', 5: 'J', 10: 'K' };

function ColorRadios({ options, value, onChange, name }) {
  return (
    <div className="flex flex-wrap gap-2 max-w-[520px]">
      {options.map(c => (
        <label key={c.name} className="flex items-center gap-1 cursor-pointer">
          <input type="radio" name={name} checked={value === c.name} onChange={() => onChange(c.name)} />
          <span className="px-2 py-0.5 rounded text-sm font-medium" style={{ background: c.hex, color: c.fg }}>{c.name}</span>
        </label>
      ))}
    </div>
  );
}

function fmt(ohms) {
  if (ohms >= 1e9) return (ohms / 1e9).toString() + ' GΩ';
  if (ohms >= 1e6) return (ohms / 1e6).toString() + ' MΩ';
  if (ohms >= 1e3) return (ohms / 1e3).toString() + ' kΩ';
  return ohms + ' Ω';
}

export default function ResistorCalc() {
  const [bands, setBands] = useState('4');
  const [b1, setB1] = useState('green');
  const [b2, setB2] = useState('red');
  const [b3, setB3] = useState('black'); // used only for 5/6 band as significant
  const [mult, setMult] = useState('blue');
  const [tol, setTol] = useState('gold');
  const [tc, setTc] = useState('brown'); // temperature coefficient (6-band)
  const [res, setRes] = useState(null);

  const calc = () => {
    const d1 = COLORS.find(c => c.name === b1).digit;
    const d2 = COLORS.find(c => c.name === b2).digit;
    let significant = d1 * 10 + d2;
    if (bands !== '4') {
      const d3 = COLORS.find(c => c.name === b3).digit;
      significant = d1 * 100 + d2 * 10 + d3;
    }
    const m = [...COLORS, ...MULT_EXTRA].find(c => c.name === mult).mult;
    const value = significant * m;
    const tolItem = TOL_ONLY.find(c => c.name === tol);
    const ppm = { brown: 100, red: 50, orange: 15, yellow: 25, blue: 10, violet: 5 }[tc];
    setRes({ value, tol: tolItem.tol, letter: TOL_LETTER[tolItem.tol], ppm, bands });
  };
  const clear = () => setRes(null);

  const sigOptions = COLORS; // 0-9

  return (
    <CalcShell look="cnet" showActions={false} title="Resistor Calculator" subtitle="Find the resistance value and tolerance of a resistor from its color-code bands.">
      <h3 className="cnet-section-title">Resistor color code calculator</h3>
      <p className="text-sm text-slate-700 mb-3">Use this calculator to find out the ohm value and tolerance based on resistor color codes.</p>
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="cnet-panel">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-bold text-slate-900">Number of Bands:</span>
            <select data-testid="resistor-bands" className="cnet-select" value={bands} onChange={e => setBands(e.target.value)}>
              <option>4</option><option>5</option><option>6</option>
            </select>
            <span>Band</span>
          </div>

          <div className="mb-3">
            <div className="font-bold text-slate-900 mb-1">1<sup>st</sup> Band Color:</div>
            <ColorRadios options={sigOptions} value={b1} onChange={setB1} name="b1" />
          </div>
          <div className="mb-3">
            <div className="font-bold text-slate-900 mb-1">2<sup>nd</sup> Band Color:</div>
            <ColorRadios options={sigOptions} value={b2} onChange={setB2} name="b2" />
          </div>
          {bands !== '4' && (
            <div className="mb-3">
              <div className="font-bold text-slate-900 mb-1">3<sup>rd</sup> Band Color:</div>
              <ColorRadios options={sigOptions} value={b3} onChange={setB3} name="b3" />
            </div>
          )}
          <div className="mb-3">
            <div className="font-bold text-slate-900 mb-1">Multiplier Color:</div>
            <ColorRadios options={[...COLORS, ...MULT_EXTRA]} value={mult} onChange={setMult} name="mult" />
          </div>
          <div className="mb-3">
            <div className="font-bold text-slate-900 mb-1">Tolerance Color:</div>
            <ColorRadios options={TOL_ONLY} value={tol} onChange={setTol} name="tol" />
          </div>
          {bands === '6' && (
            <div className="mb-3">
              <div className="font-bold text-slate-900 mb-1">Temperature Coefficient:</div>
              <ColorRadios options={COLORS.filter(c => ['brown', 'red', 'orange', 'yellow', 'blue', 'violet'].includes(c.name))} value={tc} onChange={setTc} name="tc" />
            </div>
          )}
          <CNetActions onCalc={calc} onClear={clear} testId="resistor" />
        </div>

        {/* Resistor visual + Color reference table */}
        <div>
          <svg viewBox="0 0 340 150" className="w-full">
            {/* Wire leads */}
            <line x1="0" y1="75" x2="70" y2="75" stroke="#334155" strokeWidth="2"/>
            <line x1="270" y1="75" x2="340" y2="75" stroke="#334155" strokeWidth="2"/>
            {/* Body */}
            <path d="M70,55 Q80,40 100,40 L240,40 Q260,40 270,55 L270,95 Q260,110 240,110 L100,110 Q80,110 70,95 Z" fill="#f5f5dc" stroke="#334155" strokeWidth="1.5"/>
            {/* Bands */}
            <rect x={106} y={40} width="12" height="70" fill={COLORS.find(c => c.name === b1)?.hex || '#000'} stroke="#334155" strokeWidth="0.5"/>
            <rect x={140} y={40} width="12" height="70" fill={COLORS.find(c => c.name === b2)?.hex || '#000'} stroke="#334155" strokeWidth="0.5"/>
            {bands !== '4' && <rect x={172} y={40} width="12" height="70" fill={COLORS.find(c => c.name === b3)?.hex || '#000'} stroke="#334155" strokeWidth="0.5"/>}
            <rect x={bands === '4' ? 172 : 200} y={40} width="12" height="70" fill={[...COLORS, ...MULT_EXTRA].find(c => c.name === mult)?.hex || '#000'} stroke="#334155" strokeWidth="0.5"/>
            <rect x={238} y={40} width="12" height="70" fill={TOL_ONLY.find(c => c.name === tol)?.hex || '#000'} stroke="#334155" strokeWidth="0.5"/>
            {/* Label arrows + text */}
            <line x1="112" y1="40" x2="112" y2="20" stroke="#334155" strokeWidth="1"/>
            <text x="112" y="15" fontSize="10" textAnchor="middle" fill="#334155">1st Band</text>
            <line x1="146" y1="40" x2="146" y2="20" stroke="#334155" strokeWidth="1"/>
            <text x="146" y="15" fontSize="10" textAnchor="middle" fill="#334155">2nd Band</text>
            {bands !== '4' && (<>
              <line x1="178" y1="110" x2="178" y2="128" stroke="#334155"/>
              <text x="178" y="140" fontSize="10" textAnchor="middle" fill="#334155">3rd Band</text>
            </>)}
            <line x1={bands === '4' ? 178 : 206} y1="110" x2={bands === '4' ? 178 : 206} y2="128" stroke="#334155"/>
            <text x={bands === '4' ? 178 : 206} y="140" fontSize="10" textAnchor="middle" fill="#334155">Multiplier</text>
            <line x1="244" y1="40" x2="244" y2="20" stroke="#334155"/>
            <text x="244" y="15" fontSize="10" textAnchor="middle" fill="#334155">Tolerance</text>
          </svg>
          <table className="cnet-info-table mt-3 text-xs">
            <thead>
              <tr><td>Color</td><td>1st/2nd Band</td><td>Multiplier</td><td>Tolerance</td></tr>
            </thead>
            <tbody>
              {[...COLORS, ...MULT_EXTRA].map(c => (
                <tr key={c.name}>
                  <td><span className="inline-block px-2 py-0.5 rounded" style={{ background: c.hex, color: c.fg }}>{c.name}</span></td>
                  <td>{c.digit ?? ''}{c.digit === 0 ? '0' : ''}</td>
                  <td>×{c.mult >= 1000 ? (c.mult / 1000 >= 1000 ? (c.mult / 1e6) + 'M' : (c.mult / 1000) + 'K') : c.mult}</td>
                  <td>{c.tol != null ? `±${c.tol}% (${TOL_LETTER[c.tol]})` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {res && (
        <CNetResult>
          <div className="text-2xl font-semibold text-slate-900" data-testid="resistor-result">
            Resistance = <span className="text-[#4c7b25]">{fmt(res.value)}</span> ± {res.tol}% ({res.letter})
          </div>
          <div className="text-sm text-slate-700 mt-1">
            Range: {fmt(res.value * (1 - res.tol / 100))} – {fmt(res.value * (1 + res.tol / 100))}
            {res.bands === '6' && res.ppm != null && <> · Temperature coefficient: {res.ppm} ppm/°C</>}
          </div>
        </CNetResult>
      )}

      <ParallelSection />
      <SeriesSection />
      <ConductorSection />
    </CalcShell>
  );
}

/* ---------- Parallel resistor sub-calc ---------- */
function ParallelSection() {
  const [text, setText] = useState('10,2,38,23,38,23,21');
  const [res, setRes] = useState(null);
  const calc = () => {
    const nums = text.split(/[,\s]+/).map(s => parseFloat(s)).filter(n => !isNaN(n) && n > 0);
    if (!nums.length) { setRes({ error: 'Enter at least one positive resistance.' }); return; }
    const inv = nums.reduce((a, r) => a + 1 / r, 0);
    setRes({ value: 1 / inv, count: nums.length, list: nums });
  };
  React.useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [text]);
  return (
    <>
      <h3 className="cnet-section-title mt-8">Parallel resistor calculator</h3>
      <p className="text-sm text-slate-700 mb-2">Provide all of the resistance values in parallel, separated by a comma "," and the total resistance is calculated in real time.</p>
      <div className="cnet-panel">
        <textarea data-testid="resistor-parallel-input" className="cnet-input" style={{ minHeight: 80, width: '100%' }} value={text} onChange={e => setText(e.target.value)} />
        <CNetActions onCalc={calc} onClear={() => { setText(''); setRes(null); }} testId="resistor-parallel" />
      </div>
      {res && res.error && <div className="text-red-600 mt-2">{res.error}</div>}
      {res && !res.error && (
        <CNetResult>
          <div className="text-xl font-semibold text-slate-900" data-testid="resistor-parallel-result">
            Total resistance (parallel) = <span className="text-[#4c7b25]">{fmt(res.value)}</span>
          </div>
          <div className="text-sm text-slate-700 mt-1">Using {res.count} resistors: {res.list.join(', ')} Ω</div>
        </CNetResult>
      )}
    </>
  );
}

/* ---------- Series resistors sub-calc ---------- */
function SeriesSection() {
  const [text, setText] = useState('10,2,38,23,38,23,21');
  const [res, setRes] = useState(null);
  const calc = () => {
    const nums = text.split(/[,\s]+/).map(s => parseFloat(s)).filter(n => !isNaN(n));
    if (!nums.length) { setRes({ error: 'Enter at least one resistance value.' }); return; }
    const sum = nums.reduce((a, b) => a + b, 0);
    setRes({ value: sum, count: nums.length, list: nums });
  };
  React.useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [text]);
  return (
    <>
      <h3 className="cnet-section-title mt-6">Resistors in series calculator</h3>
      <p className="text-sm text-slate-700 mb-2">Provide all of the resistance values in series separated by a comma "," to calculate the total resistance.</p>
      <div className="cnet-panel">
        <textarea data-testid="resistor-series-input" className="cnet-input" style={{ minHeight: 80, width: '100%' }} value={text} onChange={e => setText(e.target.value)} />
        <CNetActions onCalc={calc} onClear={() => { setText(''); setRes(null); }} testId="resistor-series" />
      </div>
      {res && res.error && <div className="text-red-600 mt-2">{res.error}</div>}
      {res && !res.error && (
        <CNetResult>
          <div className="text-xl font-semibold text-slate-900" data-testid="resistor-series-result">
            Total resistance (series) = <span className="text-[#4c7b25]">{fmt(res.value)}</span>
          </div>
          <div className="text-sm text-slate-700 mt-1">Using {res.count} resistors: {res.list.join(', ')} Ω</div>
        </CNetResult>
      )}
    </>
  );
}

/* ---------- Resistance of a Conductor sub-calc ---------- */
const LEN_TO_M = { meters: 1, feet: 0.3048, inches: 0.0254, yards: 0.9144, kilometers: 1000 };
const DIAM_TO_M = { inches: 0.0254, millimeters: 0.001, centimeters: 0.01, meters: 1 };
const MATERIAL_S = {
  Silver: 63000000, Copper: 59600000, Gold: 45200000, Aluminum: 37800000,
  Zinc: 16600000, Nickel: 14300000, Iron: 10100000, Platinum: 9430000,
  Tin: 9170000, Steel: 6990000, Lead: 4550000, Titanium: 2380000,
};

function ConductorSection() {
  const [length, setLength] = useState(100);
  const [lU, setLU] = useState('feet');
  const [diam, setDiam] = useState(0.05);
  const [dU, setDU] = useState('inches');
  const [cond, setCond] = useState(59600000);
  const [material, setMaterial] = useState('Copper');
  const [res, setRes] = useState(null);
  const calc = () => {
    const L = Number(length) * LEN_TO_M[lU];
    const d = Number(diam) * DIAM_TO_M[dU];
    const A = Math.PI * Math.pow(d / 2, 2);
    const sigma = Number(cond);
    if (!A || !sigma) { setRes({ error: 'Diameter and conductivity must be positive.' }); return; }
    const R = L / (sigma * A);
    setRes({ R, A, L, sigma });
  };
  React.useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [length, lU, diam, dU, cond]);
  const chooseMaterial = (m) => { setMaterial(m); setCond(MATERIAL_S[m]); };
  return (
    <>
      <h3 className="cnet-section-title mt-6">Resistance of a Conductor</h3>
      <p className="text-sm text-slate-700 mb-2">Use the following to calculate the resistance of a conductor. This calculator assumes the conductor is round.</p>
      <div className="cnet-panel">
        <div className="cnet-row" style={{ gridTemplateColumns: '160px 1fr 1fr' }}>
          <div className="cnet-label">Length</div>
          <input data-testid="resistor-cond-length" className="cnet-input" type="number" value={length} onChange={e => setLength(e.target.value)} />
          <select className="cnet-select" value={lU} onChange={e => setLU(e.target.value)}>{Object.keys(LEN_TO_M).map(k => <option key={k}>{k}</option>)}</select>
        </div>
        <div className="cnet-row" style={{ gridTemplateColumns: '160px 1fr 1fr' }}>
          <div className="cnet-label">Diameter</div>
          <input data-testid="resistor-cond-diam" className="cnet-input" type="number" step="any" value={diam} onChange={e => setDiam(e.target.value)} />
          <select className="cnet-select" value={dU} onChange={e => setDU(e.target.value)}>{Object.keys(DIAM_TO_M).map(k => <option key={k}>{k}</option>)}</select>
        </div>
        <div className="cnet-row" style={{ gridTemplateColumns: '160px 1fr auto 1fr' }}>
          <div className="cnet-label">Conductivity</div>
          <input data-testid="resistor-cond-sigma" className="cnet-input" type="number" value={cond} onChange={e => { setCond(e.target.value); setMaterial('custom'); }} />
          <span className="text-sm pt-2 px-2">S/m &nbsp;<b>Or Select Material</b></span>
          <select data-testid="resistor-cond-material" className="cnet-select" value={material} onChange={e => chooseMaterial(e.target.value)}>
            <option value="custom">(custom)</option>
            {Object.keys(MATERIAL_S).map(k => <option key={k}>{k}</option>)}
          </select>
        </div>
        <CNetActions onCalc={calc} onClear={() => setRes(null)} testId="resistor-cond" />
      </div>
      {res && res.error && <div className="text-red-600 mt-2">{res.error}</div>}
      {res && !res.error && (
        <CNetResult>
          <div className="text-xl font-semibold text-slate-900" data-testid="resistor-cond-result">
            Resistance = <span className="text-[#4c7b25]">{fmt(res.R)}</span>
          </div>
          <div className="text-sm text-slate-700 mt-1">
            R = L / (σ × A) = {res.L.toFixed(3)} m / ({res.sigma.toLocaleString()} S/m × {res.A.toExponential(3)} m²)
          </div>
        </CNetResult>
      )}
    </>
  );
}
